import { useEffect, useMemo, useRef } from 'react'
import { toast } from 'react-hot-toast'

/**
 * Distinguishes a single click from a double click on the same row.
 *
 * The browser fires `click` before `dblclick`, so a cell that means different
 * things by each has to hold the single-click action back long enough for a
 * second click to cancel it. That means live timers, which means an unmount
 * hazard: navigate away (or change a filter that unmounts the table) between
 * the two clicks and the deferred action still runs, landing its toast on
 * whatever screen the user is looking at by then.
 *
 * Both master-question tables had this logic, byte-identical and each with
 * its own raw `Map<string, Timeout>`; the cleanup had to be written twice.
 * Owning the map here means a caller never sees a timer at all.
 */
const DOUBLE_CLICK_WINDOW_MS = 200

export interface DeferredRowClicks {
  /**
   * Run `action` unless a second click on the same `key` arrives first, in
   * which case neither this nor the earlier one runs — `onDoubleClick` takes
   * over.
   */
  onSingleClick: (key: string, action: () => void) => void
  /** Cancel any click pending for `key`, then run `action` now. */
  onDoubleClick: (key: string, action: () => void) => void
}

export function useDeferredRowClicks(): DeferredRowClicks {
  const pending = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    const timers = pending.current
    return () => {
      for (const timer of timers.values()) clearTimeout(timer)
      timers.clear()
    }
  }, [])

  // Stable identity: these column definitions are built inside a `useMemo`,
  // and a fresh object every render would either churn that memo or have to be
  // silently omitted from its deps. The closures only touch a ref, so there is
  // nothing to re-create.
  return useMemo(() => {
    const cancel = (key: string): boolean => {
      const timer = pending.current.get(key)
      if (timer === undefined) return false
      clearTimeout(timer)
      pending.current.delete(key)
      return true
    }

    return {
      onSingleClick: (key: string, action: () => void) => {
        // A pending timer means this is the second click; cancelling it is the
        // whole response, since `dblclick` follows and does the real work.
        if (cancel(key)) return
        pending.current.set(
          key,
          setTimeout(() => {
            pending.current.delete(key)
            action()
          }, DOUBLE_CLICK_WINDOW_MS),
        )
      },
      onDoubleClick: (key: string, action: () => void) => {
        cancel(key)
        action()
      },
    }
  }, [])
}

/** Copy `text`, reporting either outcome — both tables did this by hand. */
export async function copyWithToast(
  text: string,
  success: string,
  failure: string,
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    toast.success(success)
  } catch {
    toast.error(failure)
  }
}
